import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import type {
  ShippingStateRow,
  ShippingTestAction,
} from "@/services/shipping-test";
import { FlaskConical, Loader2, Mail, Play, Trash2 } from "lucide-react";
import { useState } from "react";

import { useShippingTestJob } from "../hooks/use-shipping-test-job";
import { CRON_TEST_LABELS } from "../messages";
import { currentCycleLabel, isCycleLabel } from "../utils/cycle-label";

const CONFIRMED_ACTIONS: readonly ShippingTestAction[] = ["dispatch", "reset"];

function ShippingRow({ row }: { row: ShippingStateRow }): JSX.Element {
  return (
    <tr className="border-t border-amber-200">
      <td className="py-1 pr-3 font-medium">{row.type}</td>
      <td className="py-1 pr-3">
        {row.sent_at
          ? CRON_TEST_LABELS.SENT_AT(row.sent_at)
          : row.blocked_at
            ? CRON_TEST_LABELS.BLOCKED
            : CRON_TEST_LABELS.NOT_SENT}
      </td>
      <td className="py-1 pr-3">{row.attempts}</td>
      <td className="py-1">
        {CRON_TEST_LABELS.DELIVERY_COUNTS(
          row.delivered,
          row.failed,
          row.deliveries,
        )}
      </td>
    </tr>
  );
}

export function CronTestPanel(): JSX.Element {
  const [cycle, setCycle] = useState<string>(currentCycleLabel(new Date()));
  const [pending, setPending] = useState<ShippingTestAction | null>(null);

  const { run, clear, job, jobId, isSettled, isEnqueuing, error } =
    useShippingTestJob();

  const cycleIsValid: boolean = isCycleLabel(cycle);
  const isRunning: boolean = jobId !== null && !isSettled;
  const busy: boolean = isEnqueuing || isRunning;

  const start = (action: ShippingTestAction): void => {
    if (CONFIRMED_ACTIONS.includes(action)) {
      setPending(action);
      return;
    }

    void run(action, cycle);
  };

  const confirm = (): void => {
    if (pending === null) return;

    const action: ShippingTestAction = pending;
    setPending(null);
    void run(action, cycle);
  };

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4">
      <div className="mb-3 flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900">
          {CRON_TEST_LABELS.TITLE}
        </h3>
      </div>

      <p className="mb-3 text-xs text-amber-800">
        {CRON_TEST_LABELS.DESCRIPTION}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label
            htmlFor="cron-test-cycle"
            className="block text-xs font-medium text-amber-900"
          >
            {CRON_TEST_LABELS.CYCLE_LABEL}
          </label>
          <Input
            id="cron-test-cycle"
            value={cycle}
            onChange={(event) => setCycle(event.target.value.trim())}
            placeholder={CRON_TEST_LABELS.CYCLE_PLACEHOLDER}
            className="h-8 w-[130px] bg-white text-sm"
          />
        </div>

        <Button
          size="sm"
          variant="outline"
          disabled={busy || !cycleIsValid}
          onClick={() => start("generate")}
          className="h-8 border-amber-400 bg-white text-amber-900 hover:bg-amber-700 hover:text-white"
        >
          <Play className="mr-2 h-3.5 w-3.5" />
          {CRON_TEST_LABELS.GENERATE}
        </Button>

        <Button
          size="sm"
          variant="outline"
          disabled={busy || !cycleIsValid}
          onClick={() => start("dispatch")}
          className="h-8 border-amber-400 bg-white text-amber-900 hover:bg-amber-700 hover:text-white"
        >
          <Mail className="mr-2 h-3.5 w-3.5" />
          {CRON_TEST_LABELS.DISPATCH}
        </Button>

        <Button
          size="sm"
          variant="outline"
          disabled={busy || !cycleIsValid}
          onClick={() => start("reset")}
          className="h-8 border-red-300 bg-white text-red-700 hover:bg-red-700 hover:text-white"
        >
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          {CRON_TEST_LABELS.RESET}
        </Button>
      </div>

      {!cycleIsValid && (
        <p className="mt-2 text-xs text-red-600">
          {CRON_TEST_LABELS.CYCLE_INVALID}
        </p>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {job && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-white p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-medium text-amber-900">
              {CRON_TEST_LABELS.JOB_SUMMARY(job.job_id, job.action, job.state)}
            </span>
            <div className="flex items-center gap-2">
              {isRunning && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
              )}
              <button
                type="button"
                onClick={clear}
                className="text-xs text-[#3c7dd9] hover:underline"
              >
                {CRON_TEST_LABELS.CLEAR_JOB}
              </button>
            </div>
          </div>

          <Progress value={job.progress} className="h-1.5" />

          {job.failed_reason && (
            <p className="mt-2 text-xs text-red-600">{job.failed_reason}</p>
          )}

          {job.result && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-700">
                {CRON_TEST_LABELS.SNAPSHOT_STATE(job.result.state.has_snapshot)}
              </p>

              {job.result.state.shippings.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-gray-700">
                    <thead className="text-amber-900">
                      <tr>
                        <th className="pb-1 pr-3">
                          {CRON_TEST_LABELS.COLUMN_TYPE}
                        </th>
                        <th className="pb-1 pr-3">
                          {CRON_TEST_LABELS.COLUMN_SENT}
                        </th>
                        <th className="pb-1 pr-3">
                          {CRON_TEST_LABELS.COLUMN_ATTEMPTS}
                        </th>
                        <th className="pb-1">
                          {CRON_TEST_LABELS.COLUMN_DELIVERIES}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.result.state.shippings.map(
                        (row: ShippingStateRow) => (
                          <ShippingRow key={row.id} row={row} />
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={pending !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setPending(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pending === "reset"
                ? CRON_TEST_LABELS.RESET_TITLE
                : CRON_TEST_LABELS.DISPATCH_TITLE}
            </DialogTitle>
            <DialogDescription>
              {pending === "reset"
                ? CRON_TEST_LABELS.RESET_DESCRIPTION(cycle)
                : CRON_TEST_LABELS.DISPATCH_DESCRIPTION(cycle)}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setPending(null)}>
              {CRON_TEST_LABELS.CANCEL}
            </Button>
            <Button size="sm" onClick={confirm}>
              {CRON_TEST_LABELS.CONFIRM}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
